import React, { useCallback, useRef, useState } from "react"
import { Box, Typography, IconButton, Popover, Table, TableBody, TableRow, TableCell, CircularProgress, Chip } from "@mui/material"
import { reportFormNumSearches, reportFormData } from "Pages/ReportsAlerts/Reports/@signals"
import { CountUpSeeker, IconSeeker } from "@ui-seeker/core"
import { postSubmit } from "@ui-seeker/utils"
import { useReportBackendApiPath } from "Pages/ReportsAlerts/Reports/@hooks"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"

const ROW_SX = { '& td': { borderBottom: '1px solid', borderColor: 'divider', py: 1.2, px: 2 } }
const LABEL_SX = { fontWeight: 'bold', whiteSpace: 'nowrap', width: 160 }

export default function ReportFormHeaderNumSearches() {
	const isDanger = reportFormNumSearches.value >= 1000
	const { backendUrl } = useReportBackendApiPath()
	const anchorRef = useRef(null)
	const [open, setOpen] = useState(false)
	const [info, setInfo] = useState(null)
	const [loading, setLoading] = useState(false)

	const handleOpen = useCallback(() => {
		setLoading(true)
		setOpen(true)
		postSubmit(`${backendUrl}/num-searches?isReportInfo=1`, reportFormData.value)
			.then(data => setInfo(data))
			.catch(() => setInfo(null))
			.finally(() => setLoading(false))
	}, [backendUrl])

	return <>
		<Box
			sx={{ display: 'flex', alignItems: 'center', color: (reportFormNumSearches.value > 0 ? isDanger ? 'error.main' : 'green' : 'black') }}>
			<Typography variant="title" sx={{ fontWeight: 'regular', p: 1, color: 'black' }}>Num. Searches:</Typography>
			<CountUpSeeker end={reportFormNumSearches.value}/>
			{!!isDanger && <IconSeeker icon="fire" sx={{ color: 'error.main', fontSize: "1rem", px: 0.5 }}/>}
			<IconButton ref={anchorRef} data-tour-id="tour-num-searches-info" size="small" onClick={handleOpen} sx={{ ml: 0.5 }}>
				<InfoOutlinedIcon fontSize="small"/>
			</IconButton>
		</Box>

		<Popover
			open={open}
			anchorEl={anchorRef.current}
			onClose={() => setOpen(false)}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			transformOrigin={{ vertical: 'top', horizontal: 'right' }}
		>
			<Box sx={{ minWidth: 420 }}>
				{loading
					? <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={24}/></Box>
					: info
						? <Table size="small">
							<TableBody>
								<TableRow sx={ROW_SX}>
									<TableCell sx={LABEL_SX}>Num. Searches</TableCell>
									<TableCell sx={{ color: isDanger ? 'error.main' : 'success.main', fontWeight: 'bold' }}>{info.numSearches ?? '—'}</TableCell>
								</TableRow>
								<TableRow sx={ROW_SX}>
									<TableCell sx={LABEL_SX}>Num. Offices</TableCell>
									<TableCell>{info.numOffices ?? '—'}</TableCell>
								</TableRow>
								<TableRow sx={ROW_SX}>
									<TableCell sx={LABEL_SX}>Searches per Office</TableCell>
									<TableCell>{info.numSearchesPerOffice ?? '—'}</TableCell>
								</TableRow>
								<TableRow sx={ROW_SX}>
									<TableCell sx={LABEL_SX}>First Pickup Date</TableCell>
									<TableCell>{info.firstPickupDate ?? '—'}</TableCell>
								</TableRow>
								<TableRow sx={ROW_SX}>
									<TableCell sx={LABEL_SX}>Last Pickup Date</TableCell>
									<TableCell>{info.lastPickupDate ?? '—'}</TableCell>
								</TableRow>
								<TableRow sx={{ '& td': { py: 1.2, px: 2 } }}>
									<TableCell sx={{ ...LABEL_SX, verticalAlign: 'top' }}>Pickup Dates</TableCell>
									<TableCell>
										{info.pickupDates?.length
											? <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
												{info.pickupDates.map(d => (
													<Chip key={d} label={d} size="small" variant="outlined" sx={{ borderRadius: 4, fontSize: '0.75rem', color: 'primary.main', borderColor: 'primary.light', bgcolor: 'primary.50' }}/>
												))}
											</Box>
											: '—'}
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
						: <Box sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">No data available</Typography></Box>
				}
			</Box>
		</Popover>
	</>
}